import React, { useState, useEffect } from 'react';
import { DataService } from '../../services/dataService';
import { ContextualChatButton } from '../ContextualChatButton';
import { useProfile } from '../../contexts/ProfileContext';
import { WorkoutIcon, getWorkoutIconType } from '../../utils/workoutIconMapper';
import { CHILD_MOBILITY_RE } from '../../utils/sharedFormatters';
// Icon imports
import { CardioIcon, StrengthIcon, HIITIcon, YogaIcon, MobilityIcon, RestIcon, MorningIcon, MiddayIcon, AfternoonIcon, EveningIcon, CompletedIcon } from '../icons';
// Card system styles
import '../../styles/card-system.css';

interface ThisWeeksPlanCardProps {
  onItemClick: (type: string, id: string) => void;
}

/**
 * ThisWeeksPlanCard - Displays current week's plan with all sessions
 * Shows in both Today section and Goal Detail view
 * 
 * Features (Jan 2025 - 6-Fix Implementation):
 * 1. Dynamic title: "{Name}'s week of {Monday Date}" with LLM subhead
 * 2. Day dots: show day name, green for past days
 * 3. 6-category structure: cardio/strength/hiit/yoga/mobility/rest
 * 4. ALL activities in reverse chron with done indicators
 * 5. Trimmed titles (text before colon only)
 * 6. Appears in both Today section AND goal detail view
 */
export const ThisWeeksPlanCard: React.FC<ThisWeeksPlanCardProps> = ({ onItemClick }) => {
  const { profile } = useProfile(); // 🆕 JAN 5, 2026: Use profile context for user name
  
  const [weeklyPlan, setWeeklyPlan] = useState<any>(null);
  const [planData, setPlanData] = useState<{
    workouts: any[];
    mobilityRoutines: any[];
    restDays: any[];
  } | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadWeeklyPlan = async () => {
      try {
        const plan = await DataService.getCurrentWeekPlan();
        setWeeklyPlan(plan || null);
        
        if (plan) {
          const data = await DataService.getAllData();
          
          // 🔍 DEBUG: Log ALL workouts before planId filtering
          // 🔧 FIX MAR 20: Filter out rest_day child workouts (shown nested under rest day card)
          // Matches PlanDetail (line 226) and MyPath enhanced (line 1005) filtering
          const planWorkouts = data.workouts.filter((w: any) => w.planId === plan.id && w.context !== 'rest_day' && w.status !== 'replaced' && w.status !== 'skipped');
          // 🔧 CRITICAL FIX JAN 25: Filter out component mobility (warmups/cooldowns) from plan view
          // Component mobility should only be accessible via parent workout detail page
          // Check BOTH context field AND workout_id field (either indicates child)
          // 🔧 FIX MAR 20: Also filter rest_day mobility (matches PlanDetail line 237-238)
          const planMobility = data.mobilityRoutines.filter((m: any) =>
            m.planId === plan.id && m.context !== 'component' && m.context !== 'rest_day' && !m.workout_id
          );
          const planRestDays = data.restDays.filter((r: any) => r.planId === plan.id);
          
          setPlanData({
            workouts: planWorkouts,
            mobilityRoutines: planMobility,
            restDays: planRestDays
          });
        }
      } catch (error) {
        console.error('Failed to load weekly plan:', error);
      } finally {
        setLoading(false);
      }
    };

    loadWeeklyPlan();
  }, []);

  if (loading) {
    return (
      <div 
        className="mypath-card"
        style={{
          backgroundColor: 'white',
          textAlign: 'center'
        }}
      >
        <p style={{ color: '#113C2B', fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif' }}>
          Loading this week's plan...
        </p>
      </div>
    );
  }

  if (!weeklyPlan || !planData) {
    return (
      <div 
        className="mypath-card"
        style={{
          backgroundColor: 'white',
          textAlign: 'center'
        }}
      >
        <div style={{ color: '#1f6c4f', marginBottom: '12px' }}>
          <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
          </svg>
        </div>
        <p style={{ 
          color: '#113C2B',
          fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif',
          fontWeight: 500,
          marginBottom: '16px'
        }}>
          No active plan for this week
        </p>
        {/* 🎯 JAN 23, 2026: Removed chat button for cleaner UI */}
      </div>
    );
  }

  const planWorkouts = planData.workouts;
  const planMobility = planData.mobilityRoutines;
  const planRestDays = planData.restDays;
  
  // 🆕 JAN 5, 2026: Helper function to get workout icon component (matching PlanDetail)
  const getWorkoutIconComponent = (session: any) => {
    // Handle mobility sessions separately (use modality field for icon selection)
    if (session.sessionType === 'mobility') {
      const modality = (session.modality || session.type || '').toLowerCase();
      
      // Yoga modality → yoga icon
      if (modality === 'yoga' || modality.includes('yoga')) {
        return <WorkoutIcon type="yoga" size={16} className="flex-shrink-0" />;
      }
      
      // Static stretching → stretching icon
      if (modality === 'static-stretching' || modality.includes('stretch')) {
        return <WorkoutIcon type="stretching" size={16} className="flex-shrink-0" />;
      }
      
      // Default to stretching for other mobility types (joint-mobility, cooldown, etc.)
      return <WorkoutIcon type="stretching" size={16} className="flex-shrink-0" />;
    }
    
    // For non-mobility sessions, use existing logic
    const workoutType = session.type || session.workout_type || '';
    const activityType = session.activityType || '';
    
    const iconType = getWorkoutIconType(workoutType, activityType);
    const intensity = session.intensity || 0.5;
    
    return (
      <WorkoutIcon
        iconType={iconType}
        title={session.title || session.name || ''}
        size={16}
        className="flex-shrink-0"
      />
    );
  };

  // 🔧 FIX JAN 28, 2026: Calculate CURRENT CALENDAR WEEK boundaries (Monday-Sunday)
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
  
  const todayDayOfWeek = today.getDay(); // 0=Sunday, 1=Monday, ..., 6=Saturday
  
  // Calculate Monday of this week
  // If today is Sunday (0), go back 6 days. Otherwise go back (dayOfWeek - 1) days
  const daysToMonday = todayDayOfWeek === 0 ? 6 : todayDayOfWeek - 1;
  const currentWeekStart = new Date(today);
  currentWeekStart.setDate(today.getDate() - daysToMonday);
  currentWeekStart.setHours(0, 0, 0, 0);
  
  // 🔧 FIX JAN 23: Use local date (not UTC) for week boundaries
  const currentWeekStartStr = `${currentWeekStart.getFullYear()}-${String(currentWeekStart.getMonth() + 1).padStart(2, '0')}-${String(currentWeekStart.getDate()).padStart(2, '0')}`;
  
  // Calculate Sunday of this week (Monday + 6 days)
  const currentWeekEnd = new Date(currentWeekStart);
  currentWeekEnd.setDate(currentWeekStart.getDate() + 6);
  currentWeekEnd.setHours(23, 59, 59, 999);
  
  // 🔧 FIX JAN 23: Use local date (not UTC) for week boundaries
  const currentWeekEndStr = `${currentWeekEnd.getFullYear()}-${String(currentWeekEnd.getMonth() + 1).padStart(2, '0')}-${String(currentWeekEnd.getDate()).padStart(2, '0')}`;
  
  // 🔧 FIX FEB 9, 2026: Helper to get LOCAL date string from any date input
  // CRITICAL: When input is a "YYYY-MM-DD" string, extract directly (substring)
  // to avoid new Date("2026-02-10") = UTC midnight → EST getDate() = Feb 9 (WRONG!)
  const toLocalDateStr = (dateInput: string | Date): string => {
    if (typeof dateInput === 'string' && dateInput.match(/^\d{4}-\d{2}-\d{2}/)) {
      return dateInput.substring(0, 10);
    }
    const d = dateInput instanceof Date ? dateInput : new Date(dateInput);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  };

  // Helper to get sessions for a specific day BY DATE STRING
  const getSessionsForDay = (dayDateStr: string) => {
    const allSessions = [
      ...planWorkouts.map(w => ({ ...w, sessionType: 'workout' })),
      ...planMobility.map(m => ({ ...m, sessionType: 'mobility' })),
      ...planRestDays.map(r => ({ ...r, sessionType: 'rest' }))
    ];
    
    return allSessions.filter((session: any) => {
      if (!session.scheduledDate) return false;
      // 🔧 FIX FEB 9, 2026: Use toLocalDateStr to avoid UTC timezone shift
      const sessionDateStr = toLocalDateStr(session.scheduledDate);
      return sessionDateStr === dayDateStr;
    });
  };
  
  const hasSessions = (sessions: any[]) => sessions.length > 0;
  const areAllSessionsDone = (sessions: any[]) => sessions.length > 0 && sessions.every((s: any) => s.status === 'completed');
  
  // 🔧 FIX JAN 23: Filter to CURRENT CALENDAR WEEK ONLY for legend counts
  // 🔧 FIX FEB 9, 2026: Use toLocalDateStr to avoid UTC timezone shift
  const thisWeekWorkouts = planWorkouts.filter((w: any) => {
    if (!w.scheduledDate) return false;
    const workoutDateStr = toLocalDateStr(w.scheduledDate);
    return workoutDateStr >= currentWeekStartStr && workoutDateStr <= currentWeekEndStr;
  });
  
  // 🔧 FIX JAN 23: Filter mobility to CURRENT CALENDAR WEEK ONLY
  // 🔧 FIX FEB 9, 2026: Use toLocalDateStr to avoid UTC timezone shift
  const thisWeekMobility = planMobility.filter((m: any) => {
    if (!m.scheduledDate) return false;
    const mobDateStr = toLocalDateStr(m.scheduledDate);
    return mobDateStr >= currentWeekStartStr && mobDateStr <= currentWeekEndStr;
  });
  
  // 🔧 FIX JAN 23: Filter rest to CURRENT CALENDAR WEEK ONLY
  // 🔧 FIX FEB 9, 2026: Use toLocalDateStr to avoid UTC timezone shift
  const thisWeekRestDays = planRestDays.filter((r: any) => {
    if (!r.scheduledDate) return false;
    const restDateStr = toLocalDateStr(r.scheduledDate);
    return restDateStr >= currentWeekStartStr && restDateStr <= currentWeekEndStr;
  });

  const thisWeekCompletedActivities = thisWeekWorkouts.filter((w: any) => w.status === 'completed').length + 
                                     thisWeekMobility.filter((m: any) => m.status === 'completed').length +
                                     thisWeekRestDays.filter((r: any) => r.status === 'completed').length;

  const currentDay = Math.min(thisWeekCompletedActivities + 1, 7);

  return (
    <>
      {/* 🎨 JAN 26, 2026: Single card pattern - use 'background' to override gradient */}
      <div 
        className="relative mypath-card shadow-sm cursor-pointer"
        style={{ 
          background: 'white', // Override gradient (must use 'background' not 'backgroundColor')
          border: '1px solid var(--cedar-limestone)',
          minHeight: '140px'
        }}
        onClick={() => onItemClick('plan', weeklyPlan.id)}
      >
        {/* Header with dynamic title and subhead */}
        <div className="flex items-start justify-between mb-3">
          <div className="flex-1">
            {/* 🎯 JAN 25, 2026: Use LLM-generated title if available, with robust name fallback */}
            <h3 className="mypath-card-title mb-1" style={{ fontSize: '18px' }}>
              {(() => {
                // Priority 1: Use LLM-generated week_title if available (e.g., "Let's get it going!")
                if (weeklyPlan.title && !weeklyPlan.title.includes('Weekly Plan') && !weeklyPlan.title.includes('Goal Plan')) {
                  return weeklyPlan.title;
                }
                
                // Priority 2: Fallback to date-based title with user name
                // 🔧 JAN 28: CRITICAL FIX - Use CURRENT WEEK'S MONDAY (not plan's weekStartDate)
                // Issue: plan.weekStartDate might be the plan's first week (e.g., Jan 20)
                // But THIS card shows THIS week, so use THIS week's Monday
                const currentWeekMonday = new Date(currentWeekStart);
                
                const monthName = currentWeekMonday.toLocaleDateString('en-US', { month: 'short' });
                const day = currentWeekMonday.getDate();
                
                // 🔧 JAN 25: Check BOTH camelCase AND snake_case (API inconsistency)
                const userName = (profile as any)?.preferredName || 
                                (profile as any)?.preferred_name || 
                                (profile as any)?.firstName || 
                                (profile as any)?.first_name || 
                                'Your';
                
                // Possessive apostrophe logic:
                // - "Your" → no apostrophe
                // - Names ending in 's' → just apostrophe (Chris' not Chris's)
                // - Other names → apostrophe + s (Paul's)
                let possessive = '';
                if (userName !== 'Your') {
                  possessive = userName.endsWith('s') ? "'" : "'s";
                }
                const dayWithSuffix = day === 1 || day === 21 || day === 31 ? `${day}st` 
                  : day === 2 || day === 22 ? `${day}nd`
                  : day === 3 || day === 23 ? `${day}rd`
                  : `${day}th`;
                return `${userName}${possessive} week of ${monthName} ${dayWithSuffix}`;
              })()}
            </h3>
            {/* 🎯 JAN 23, 2026: Description only in plan detail, NOT on card */}
          </div>
          {/* 🎯 JAN 23, 2026: Removed chat button for cleaner UI */}
        </div>

        {/* 🔧 FIX JAN 28, 2026: Show ALL 7 DAYS (Monday-Sunday) of CURRENT CALENDAR WEEK */}
        <div className="mb-3 pb-3 border-b border-gray-100">
          <div className="flex justify-between items-end">
            {Array.from({ length: 7 }, (_, dayIndex) => {
              // dayIndex: 0=Monday, 1=Tuesday, ..., 6=Sunday
              const dayDate = new Date(currentWeekStart);
              dayDate.setDate(currentWeekStart.getDate() + dayIndex);
              
              // 🔧 FIX JAN 23: Use local date (not UTC) to avoid timezone shift
              const dayDateStr = `${dayDate.getFullYear()}-${String(dayDate.getMonth() + 1).padStart(2, '0')}-${String(dayDate.getDate()).padStart(2, '0')}`;
              
              const daySessions = getSessionsForDay(dayDateStr);
              const hasWorkouts = hasSessions(daySessions);
              const allDone = areAllSessionsDone(daySessions);
              const isToday = dayDateStr === todayStr;
              const isPast = dayDateStr < todayStr;

              // Dot color system (matches native ThisWeeksPlanCard):
              //   Teal (#42A5A5)         = rest day (always, even if past)
              //   Cedar Prime (#1F6C4F)  = completed past day + white checkmark
              //   Cedar Mist (#A8D1BF)   = missed/incomplete past workout day
              //   Cedar Light (#5FA287)  = today/future with workout
              //   Gray (#E0E0E0)         = no activity scheduled

              // Filter out component mobility FIRST so dot color + icon use the same data
              const filteredSessions = daySessions.filter((s: any) => {
                if (s.sessionType === 'mobility') {
                  if (s.context === 'component' || s.context === 'rest_day' || s.workout_id) return false;
                  const t = (s.title || '').toLowerCase();
                  if (CHILD_MOBILITY_RE.test(t)) return false;
                }
                return true;
              });
              const hasRestDay = filteredSessions.some((s: any) => s.sessionType === 'rest');

              let dotColor = 'bg-[#E0E0E0]'; // Default: no activity
              let showCheckmark = false;

              if (hasRestDay) {
                dotColor = 'bg-[#42A5A5]'; // Teal — rest day (always)
              } else if (isPast && hasWorkouts && allDone) {
                dotColor = 'bg-[#1F6C4F]'; // Cedar Prime — completed
                showCheckmark = true;
              } else if (isPast && hasWorkouts) {
                dotColor = 'bg-[#A8D1BF]'; // Cedar Mist — missed/incomplete
              } else if (hasWorkouts) {
                dotColor = 'bg-[#5FA287]'; // Cedar Light — today/future with workout
              }
              
              // 🎨 FIX JAN 23: Today is 50% larger (was 25%)
              const dotSize = isToday ? 'w-[18px] h-[18px]' : 'w-3 h-3';
              const dayAbbrevs = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];
              
              return (
                <div key={dayIndex} className="flex flex-col items-center flex-1">
                  {/* 🎨 Dot with optional checkmark */}
                  <div className="relative mb-1">
                    <div className={`rounded-full transition-all duration-200 ${dotSize} ${dotColor}`} />
                    {showCheckmark && (
                      <svg 
                        className="absolute inset-0 m-auto" 
                        width={isToday ? "12" : "8"} 
                        height={isToday ? "12" : "8"} 
                        viewBox="0 0 12 12" 
                        fill="none"
                      >
                        <path 
                          d="M10 3L4.5 8.5L2 6" 
                          stroke="white" 
                          strokeWidth="2" 
                          strokeLinecap="round" 
                          strokeLinejoin="round"
                        />
                      </svg>
                    )}
                  </div>
                  
                  {/* Primary activity icon below dot — one icon per day (matches native) */}
                  {hasWorkouts && filteredSessions.length > 0 && (
                    <div className="flex items-center gap-px mb-1 justify-center">
                      {(() => {
                        // Rest day takes priority
                        const restSession = filteredSessions.find((s: any) => s.sessionType === 'rest');
                        if (restSession) {
                          return (
                            <span
                              className="text-[10px] font-semibold px-1 py-0.5 rounded"
                              style={{ color: 'var(--cedar-accent-sky)', backgroundColor: 'rgba(66, 165, 165, 0.1)' }}
                            >
                              REST
                            </span>
                          );
                        }
                        // Pick primary: strength > hiit > cardio > mobility
                        // Resolve to canonical icon type FIRST so "full-body-strength" → "weightlifting"
                        const PRIORITY = ['weightlifting', 'bodyweight', 'core', 'crossfit', 'hiit', 'running', 'cycling', 'swimming', 'walking'];
                        const primary = [...filteredSessions]
                          .filter((s: any) => s.sessionType !== 'mobility')
                          .sort((a: any, b: any) => {
                            const aType = getWorkoutIconType(a.type || a.activityType || '', a.title || '');
                            const bType = getWorkoutIconType(b.type || b.activityType || '', b.title || '');
                            const aIdx = PRIORITY.indexOf(aType);
                            const bIdx = PRIORITY.indexOf(bType);
                            return (aIdx === -1 ? 99 : aIdx) - (bIdx === -1 ? 99 : bIdx);
                          })[0] || filteredSessions[0];
                        return <div>{getWorkoutIconComponent(primary)}</div>;
                      })()}
                    </div>
                  )}
                  
                  {/* Day Letter - Bold for today */}
                  <span className={`text-xs text-gray-500 ${isToday ? 'font-bold' : 'font-medium'}`}>
                    {dayAbbrevs[dayIndex]}
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        {/* 🎯 JAN 25: 2-Row Activity Summary - Count in text beside icon */}
        {(() => {
          // Classify workouts using getWorkoutIconType (same as native + dot icons)
          const STRENGTH_ICONS = new Set(['weightlifting', 'core', 'bodyweight', 'crossfit', 'hiit']);
          const MOBILITY_ICONS = new Set(['stretching', 'yoga']);
          let cardioCount = 0, strengthCount = 0, workoutMobilityCount = 0;
          thisWeekWorkouts.forEach((w: any) => {
            const iconType = getWorkoutIconType(w.type || w.workout_type || '', w.activityType || '');
            if (STRENGTH_ICONS.has(iconType)) strengthCount++;
            else if (MOBILITY_ICONS.has(iconType)) workoutMobilityCount++;
            else cardioCount++;
          });
          const mobilityCount = thisWeekMobility.length + workoutMobilityCount;
          const restCount = thisWeekRestDays.length;
          
          // 🎯 JAN 25: Only show categories with count > 0, group into 2 rows
          const row1 = [
            { icon: CardioIcon, label: 'Cardio', count: cardioCount, color: 'var(--colorrehabtxt)' },
            { icon: StrengthIcon, label: 'Strength', count: strengthCount, color: 'var(--coloryogatxt)' }
          ].filter(cat => cat.count > 0);
          
          const row2 = [
            { icon: MobilityIcon, label: 'Mobility', count: mobilityCount, color: 'var(--cedar-primary)' },
            { type: 'rest', label: 'Rest', count: restCount, color: 'var(--cedar-accent-sky)' }
          ].filter(cat => cat.count > 0);
          
          const hasAnyCategories = row1.length > 0 || row2.length > 0;
          
        return hasAnyCategories && (
          <div className="flex flex-col items-center space-y-2 text-sm">
            {/* Row 1: Cardio + Strength */}
            {row1.length > 0 && (
              <div className="grid grid-cols-2 gap-x-6 w-full max-w-[280px]">
                {row1.map((cat, index) => {
                  const IconComponent = cat.icon;
                  if (!IconComponent) return null;
                  return (
                    <div key={index} className="flex items-center gap-x-2">
                      <div className="mypath-activity-icon flex-shrink-0">
                        <IconComponent size={16} color={cat.color} />
                      </div>
                      <span style={{ color: cat.color }} className="font-semibold">
                        {cat.count} {cat.label}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
            
            {/* Row 2: Mobility + Rest */}
            {row2.length > 0 && (
              <div className="grid grid-cols-2 gap-x-6 w-full max-w-[280px]">
                {row2.map((cat, index) => {
                  // 🔧 JAN 26: REST badge instead of palm tree icon
                  if (cat.type === 'rest') {
                    return (
                      <div key={index} className="flex items-center gap-x-2">
                        <span 
                          className="text-[10px] font-semibold px-1 py-0.5 rounded flex-shrink-0"
                          style={{ color: 'var(--cedar-accent-sky)', backgroundColor: 'rgba(66, 165, 165, 0.1)' }}
                        >
                          REST
                        </span>
                        <span style={{ color: cat.color }} className="font-semibold">
                          {cat.count} {cat.label}
                        </span>
                      </div>
                    );
                  }
                  
                  const IconComponent = 'icon' in cat ? cat.icon : null;
                  if (!IconComponent) return null;
                  return (
                    <div key={index} className="flex items-center gap-x-2">
                      <div className="mypath-activity-icon flex-shrink-0">
                        <IconComponent size={16} color={cat.color} />
                      </div>
                      <span style={{ color: cat.color }} className="font-semibold">
                        {cat.count} {cat.label}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
        })()}
        
        {/* 🆕 JAN 5, 2026: Removed workout list - summary is enough, click card to see details */}
      </div>
    </>
  );
};

